import React, {PropsWithChildren, useEffect, useState} from 'react';
import {useHalState} from '@synako/haligator';
import i18next from 'i18next';
import {Locale, setDefaultOptions} from 'date-fns';
import {fr} from 'date-fns/locale/fr';
import {enUS} from 'date-fns/locale/en-US';
import {de} from 'date-fns/locale/de';

// Mapping des codes de langue vers les locales date-fns
const dateFnsLocales: Record<string, Locale> = {
  fr,
  en: enUS,
  de,
};

/**
 * Fonction centralisée pour gérer le changement de langue
 * Met à jour à la fois i18next et date-fns
 */
const setLanguage = async (language: string) => {
  // Mettre à jour date-fns avec la locale appropriée
  if (language in dateFnsLocales) {
    const dateFnsLocale = dateFnsLocales[language];
    setDefaultOptions({locale: dateFnsLocale});
  }

  // Mettre à jour i18next si nécessaire
  if (language !== i18next.language) {
    await i18next.changeLanguage(language);
  }
};

/**
 * Provider qui gère le changement de langue basé sur les préférences du client
 * Surveille les changements de langue dans les données du client et met à jour i18next et date-fns
 */
export const ClientLanguageProvider: React.FC<
  PropsWithChildren<{
    clientHook: ReturnType<typeof useHalState>;
    loadingComponent: React.ReactNode;
  }>
> = ({children, clientHook, loadingComponent}) => {
  const [languageInitialized, setLanguageInitialized] = useState(false);

  useEffect(() => {
    // Si le clientHook est en cours de chargement, on ne fait rien
    if (clientHook.loading) {
      return;
    }

    // Une fois le chargement terminé, on vérifie les données du client
    if (!clientHook.data) {
      // S'il n'y a pas de données (erreur ou pas de client), on s'assure que la langue par défaut est fr
      setLanguage('fr').finally(() => {
        setLanguageInitialized(true);
      });
      return;
    }

    const langue = clientHook.data.tolede.langue_des_documents;

    // Appliquer la langue du client ou fallback sur français
    setLanguage(langue ?? 'fr').finally(() => {
      setLanguageInitialized(true);
    });

    // Lorsque le composant est démonté, on repasse en français (mode assistance)
    return () => {
      setLanguage('fr');
    };
  }, [clientHook.data, clientHook.loading]);

  if (!languageInitialized) {
    // Pendant que la langue n'est pas initialisée, on peut afficher un loader
    // pour éviter d'afficher l'application avec la mauvaise langue
    return loadingComponent;
  }

  return <>{children}</>;
};

export default ClientLanguageProvider;
